import { useState, useRef, useEffect } from 'react';
import { LocationForm } from './LocationForm';
import { CalendarPreviewProps } from '../../types';

export function CalendarPreview({
  calendarImages,
  location,
  formPosition,
  altText,
  onFieldChange,
  onMouseDown,
  onMouseMove,
  onMouseUp
}: CalendarPreviewProps) {
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef<{ x: number; y: number; scale: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const selectedImage = calendarImages.find(img => img.id === location.calendarImage);
  const imageUrl = selectedImage?.url || calendarImages[0]?.url;
  const scale = location.formScale || 1;
  const opacity = location.formOpacity ?? 0.2;

  useEffect(() => {
    if (containerRef.current) {
      const updateWidth = () => {
        setContainerWidth(containerRef.current?.offsetWidth || 0);
      };
      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  // Don't render if there's no image available
  if (!imageUrl) {
    return null;
  }

  const scaleFactor = containerWidth > 0 ? containerWidth / 500 : 1;

  const handleResizeStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scale: location.formScale || 1
    };
  };

  const handleResizeMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isResizing || !resizeStartRef.current) return;

    const deltaY = e.clientY - resizeStartRef.current.y;
    const scaleChange = deltaY / 200;
    const newScale = Math.max(0.2, Math.min(2, resizeStartRef.current.scale + scaleChange));

    onFieldChange('formScale', newScale.toString());
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    resizeStartRef.current = null;
  };

  return (
    <div
      ref={containerRef}
      className="relative rounded-xl overflow-hidden shadow-md border border-gray-200 aspect-square"
      onMouseMove={(e) => {
        if (isResizing) {
          handleResizeMove(e);
        } else {
          onMouseMove(e, scaleFactor);
        }
      }}
      onMouseUp={() => {
        handleResizeEnd();
        onMouseUp();
      }}
      onMouseLeave={() => {
        handleResizeEnd();
        onMouseUp();
      }}
    >
      <img
        src={imageUrl}
        alt={altText}
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/30"></div>
      <div
        className="absolute cursor-move"
        style={{
          left: `calc(50% + ${formPosition.x * scaleFactor}px)`,
          top: `calc(50% + ${formPosition.y * scaleFactor}px)`,
          transform: `translate(-50%, -50%) scale(${scale * scaleFactor})`
        }}
        onMouseDown={onMouseDown}
      >
        <div
          className="backdrop-blur-sm rounded-lg p-4 w-80 relative"
          style={{ backgroundColor: `rgba(255, 255, 255, ${opacity})` }}
        >
          <LocationForm location={location} onFieldChange={onFieldChange} />
          <div
            className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize hover:bg-blue-500/30 rounded-br-lg transition-colors flex items-end justify-end pb-1 pr-1"
            onMouseDown={handleResizeStart}
            title="Drag to resize"
          >
            <svg className="w-3 h-3 text-gray-600" fill="currentColor" viewBox="0 0 16 16">
              <path d="M14 14V2H2v12h12zM2 0h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z"/>
              <path d="M5 14H3v-2h2v2zm4 0H7v-2h2v2zm4 0h-2v-2h2v2z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
